import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import "./Tasks.css";
import toast from "react-hot-toast"; // Add this line

// Helper to convert date strings to timestamps for the API
const convertDateToTimestamp = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).getTime();
};

// Helper to convert timestamps to date strings for inputs
const convertTimestampToDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toISOString().split('T')[0];
};

const TaskListForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fieldId, taskListId } = useParams();
  const location = useLocation();
  const { userData, token } = useContext(UserContext); // Get user data and token
  const [isSubmitting, setIsSubmitting] = useState(false); // Add submitting state

  // Check if we're in edit mode
  const isEditMode = !!taskListId;
  const existingTaskList = location.state?.taskList;

  // The path to return to after saving
  const returnTo = location.state?.returnTo || `/tasks`;

  const [newList, setNewList] = useState({
    name: "",
    fromDate: "",
    toDate: "",
    notes: "",
  });

  // Load existing task list data if in edit mode
  useEffect(() => {
    if (isEditMode && existingTaskList) {
      setNewList({
        name: existingTaskList.name || "",
        fromDate: convertTimestampToDate(existingTaskList.fromDate),
        toDate: convertTimestampToDate(existingTaskList.toDate),
        notes: existingTaskList.notes || existingTaskList.description || "",
      });
    } else if (isEditMode && taskListId && token) {
      // If no state passed, fetch the task list from API
      const fetchTaskList = async () => {
        try {
          const response = await axios.get(`/api/taskList/${taskListId}`, {
            headers: { Authorization: token }
          });
          const taskList = response.data;
          setNewList({
            name: taskList.name || "",
            fromDate: convertTimestampToDate(taskList.fromDate),
            toDate: convertTimestampToDate(taskList.toDate),
            notes: taskList.notes || taskList.description || "",
          });
        } catch (error) {
          console.error("Failed to fetch task list:", error);
          toast.error(t("Failed to load task list"));
        }
      };
      fetchTaskList();
    }
  }, [isEditMode, taskListId, existingTaskList, token, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewList((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userData?.Farm?.id || !token) {
      toast.error(t("Session invalid. Please login again."));
      return;
    }

    setIsSubmitting(true);

    const payload = {
      name: newList.name,
      fromDate: convertDateToTimestamp(newList.fromDate),
      toDate: convertDateToTimestamp(newList.toDate),
      farmId: userData.Farm.id,
      tenantId: userData.Tenant.id,
      note: newList.notes,
    };

    try {
      if (isEditMode) {
        // Update existing task list
        await axios.put(`/api/taskList/${taskListId}`, payload, {
          headers: { Authorization: token },
        });
        toast.success(t("Task list updated successfully!"));
      } else {
        // Create new task list
        const response = await axios.post("/api/taskList", payload, {
          headers: { Authorization: token },
        });
        const createdList = response.data.items[0];
        toast.success(t("Task List Created!"));
      }

      // Navigate back with refresh flag
      navigate(returnTo, { state: { refresh: true } });
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} task list:`, error);
      toast.error(t(`Failed to ${isEditMode ? 'update' : 'create'} task list`));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <header className="view-header">
          <h1 className="view-header-title-group">
            {isEditMode ? t("Edit Task List") : t("Add New List")}
          </h1>
        </header>
        <form onSubmit={handleSubmit} className="task-list-form">
          <div className="form-group">
            <label htmlFor="name">{t("List Name")} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={newList.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fromDate">{t("From")} *</label>
              <input
                type="date"
                id="fromDate"
                name="fromDate"
                value={newList.fromDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="toDate">{t("To")} *</label>
              <input
                type="date"
                id="toDate"
                name="toDate"
                value={newList.toDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="notes">{t("Notes")}</label>
            <textarea
              id="notes"
              name="notes"
              rows="3"
              value={newList.notes}
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={() => navigate(-1)}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("Saving...") : t("Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskListForm;
